import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CreatorNavbar from '../components/dashboard/CreatorNavbar';
import Footer from '../components/Footer';
import { sponsorshipAPI } from '../api';
import api from '../api';

const SponsorshipEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    projectOverview: '',
    projectDescription: '',
    amountRequired: '',
    interest: ''
  });
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [charCount, setCharCount] = useState(0);
  const [interests, setInterests] = useState([]);

  const parseInterestString = (interestString) => {
    if (!interestString) {
      return [];
    }
    return interestString
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetching(true);
        const [sponsorship, interestList] = await Promise.all([
          sponsorshipAPI.getSponsorshipById(id),
          api.get('/interests').then(r => r.data).catch(() => [])
        ]);

        if (sponsorship.status !== 'DRAFT') {
          alert('Only draft sponsorships can be edited.');
          navigate(`/dashboard/creator/sponsorships/${id}`);
          return;
        }

        setFormData({
          projectOverview: sponsorship.projectOverview || '',
          projectDescription: sponsorship.projectDescription || '',
          amountRequired: sponsorship.amountRequired?.toString() || '',
          interest: ''
        });
        setSelectedInterests(parseInterestString(sponsorship.interest));
        setCharCount((sponsorship.projectOverview || '').length);
        setInterests(interestList);
      } catch (error) {
        alert('Failed to load sponsorship.');
        navigate('/dashboard/creator/sponsorships');
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    setCharCount(formData.projectOverview.length);
  }, [formData.projectOverview]);

  const handleOverviewChange = (e) => {
    setFormData({ ...formData, projectOverview: e.target.value });
  };

  const handleInterestSelect = (e) => {
    const selected = e.target.value;
    if (!selected || selectedInterests.includes(selected)) {
      return;
    }
    setSelectedInterests((prev) => [...prev, selected]);
    setFormData((prev) => ({ ...prev, interest: '' }));
  };

  const removeSelectedInterest = (interestToRemove) => {
    setSelectedInterests((prev) => prev.filter((interest) => interest !== interestToRemove));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.projectOverview.trim()) {
      newErrors.projectOverview = 'Project overview is required';
    } else if (charCount < 50 || charCount > 100) {
      newErrors.projectOverview = 'Project overview must be between 50 and 100 characters';
    }

    if (!formData.projectDescription.trim()) {
      newErrors.projectDescription = 'Project description is required';
    }

    if (!formData.amountRequired || parseFloat(formData.amountRequired) <= 0) {
      newErrors.amountRequired = 'Amount required must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      await sponsorshipAPI.updateSponsorship(id, {
        projectOverview: formData.projectOverview,
        projectDescription: formData.projectDescription,
        amountRequired: parseFloat(formData.amountRequired),
        interest: selectedInterests.length ? selectedInterests.join(', ') : null
      });
      navigate(`/dashboard/creator/sponsorships/${id}`);
    } catch (error) {
      alert('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndPublish = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      await sponsorshipAPI.updateSponsorship(id, {
        projectOverview: formData.projectOverview,
        projectDescription: formData.projectDescription,
        amountRequired: parseFloat(formData.amountRequired),
        interest: selectedInterests.length ? selectedInterests.join(', ') : null
      });
      await sponsorshipAPI.publishSponsorship(id);
      navigate(`/dashboard/creator/sponsorships/${id}`);
    } catch (error) {
      alert('Failed to save and publish. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <CreatorNavbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading sponsorship...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <CreatorNavbar />

      <main className="flex-grow container mx-auto px-4 py-8 mt-20">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(`/dashboard/creator/sponsorships/${id}`)}
              className="mb-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Sponsorship
            </button>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Edit Sponsorship</h1>
            <p className="text-gray-600">
              Update your draft sponsorship before publishing
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSave} className="bg-white rounded-lg shadow p-6 space-y-6">
            {/* Project Overview */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Project Overview *
                </label>
                <span
                  className={`text-sm font-semibold px-3 py-1 rounded-full ${
                    charCount === 0
                      ? 'bg-gray-100 text-gray-600'
                      : charCount < 50
                      ? 'bg-yellow-100 text-yellow-800'
                      : charCount > 100
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {charCount} / 50-100 characters
                </span>
              </div>
              <textarea
                value={formData.projectOverview}
                onChange={handleOverviewChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  errors.projectOverview ? 'border-red-500' : 'border-gray-300'
                }`}
                rows={4}
                maxLength={150}
                placeholder="Brief overview of your project (50-100 characters)"
              />
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {charCount === 0
                    ? 'Start typing your project overview...'
                    : charCount < 50
                    ? `Add ${50 - charCount} more character${50 - charCount === 1 ? '' : 's'} to reach minimum`
                    : charCount > 100
                    ? `Remove ${charCount - 100} character${charCount - 100 === 1 ? '' : 's'} to meet maximum`
                    : '✓ Character count is perfect! A concise summary that will attract brands.'}
                </p>
              </div>
              {errors.projectOverview && (
                <p className="mt-1 text-sm text-red-600">{errors.projectOverview}</p>
              )}
            </div>

            {/* Interest/Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Interest Category
              </label>
              <select
                value={formData.interest}
                onChange={handleInterestSelect}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={fetching}
              >
                <option value="">Select interests (optional)</option>
                {interests
                  .filter((interest) => !selectedInterests.includes(interest.item))
                  .map((interest) => (
                  <option key={interest.id} value={interest.item}>
                    {interest.category} - {interest.item}
                  </option>
                ))}
              </select>
              {selectedInterests.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Selected categories</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedInterests.map((interest) => (
                      <span
                        key={interest}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm"
                      >
                        {interest}
                        <button
                          type="button"
                          onClick={() => removeSelectedInterest(interest)}
                          className="text-blue-700 hover:text-blue-900 font-bold leading-none"
                          aria-label={`Remove ${interest}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <p className="mt-2 text-sm text-gray-500">
                Select one or more categories to help brands discover your sponsorship
              </p>
            </div>

            {/* Project Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Detailed Project Description *
              </label>
              <textarea
                value={formData.projectDescription}
                onChange={(e) => setFormData({ ...formData, projectDescription: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  errors.projectDescription ? 'border-red-500' : 'border-gray-300'
                }`}
                rows={8}
                placeholder="Provide detailed information about your project, goals, timeline, and expected outcomes..."
              />
              <p className="mt-2 text-sm text-gray-500">
                Explain what you'll create, why it matters, and how brands will benefit
              </p>
              {errors.projectDescription && (
                <p className="mt-1 text-sm text-red-600">{errors.projectDescription}</p>
              )}
            </div>

            {/* Amount Required */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount Required *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-2.5 text-gray-500">₹</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amountRequired}
                  onChange={(e) => setFormData({ ...formData, amountRequired: e.target.value })}
                  className={`w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.amountRequired ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Total funding needed for your project
              </p>
              {errors.amountRequired && (
                <p className="mt-1 text-sm text-red-600">{errors.amountRequired}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate(`/dashboard/creator/sponsorships/${id}`)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={handleSaveAndPublish}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:bg-blue-400"
                disabled={loading}
              >
                {loading ? 'Publishing...' : 'Save & Publish'}
              </button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SponsorshipEditPage;


